import { useState } from "react";
import { AlertTriangle, Check, Download, Minus, Sparkles, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAnnexMutations, useAnnexRequests } from "../hooks";
import type {
  PowerProject, ZEAnnexChecklistRow, ZEAnnexRequest, ZEAnnexScope, ZEAnnexStatus,
} from "../types";
import { ZE_ANNEX_SCOPE_LABEL } from "../types";
import { fmtDate, fmtDateTime } from "../utils";
import { AttachmentLinks, EmptyState, MultiFileInput, SectionCard } from "./ui";

const STATUS_COLOR: Record<ZEAnnexStatus, "gray" | "blue" | "green" | "red"> = {
  draft: "gray",
  sent: "blue",
  signed: "green",
  rejected: "red",
};

const CHECK_ICON: Record<ZEAnnexChecklistRow["state"], JSX.Element> = {
  ok: <Check size={13} className="text-[var(--color-success)]" />,
  missing: <AlertTriangle size={13} className="text-[var(--color-primary)]" />,
  manual: <Minus size={13} className="text-[var(--color-text-muted)]" />,
};

/** Lista „Wymagane załączniki" ze wzoru operatora. Nic nie blokuje wysyłki —
 *  pokazuje, co koordynator pomija, jeśli składa wniosek mimo braków. */
function Checklist({ rows }: { rows: ZEAnnexChecklistRow[] }) {
  const missing = rows.filter((r) => r.state === "missing").length;
  return (
    <div className="space-y-1">
      <p className="section-label">
        Wymagane załączniki {missing > 0 ? `— braki: ${missing}` : "— komplet"}
      </p>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-start gap-1.5 text-xs">
            <span className="mt-0.5">{CHECK_ICON[row.state]}</span>
            <span
              className={
                row.state === "missing"
                  ? "text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-secondary)]"
              }
            >
              {row.label}
              {row.detail ? ` — ${row.detail}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Rozstrzygnięcie wniosku: aneks zawarty (z numerem i terminem Z ANEKSU, bo
 *  operator bywa, że skraca) albo odmowa. */
function ResolveForm({ annex, connection }: { annex: ZEAnnexRequest; connection: string }) {
  const m = useAnnexMutations(connection);
  const [annexNumber, setAnnexNumber] = useState(annex.annex_number);
  const [deadline, setDeadline] = useState(annex.signed_deadline ?? annex.proposed_deadline ?? "");
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="space-y-1">
        <span className="section-label block">Nr aneksu</span>
        <input
          className="input-base w-36"
          value={annexNumber}
          onChange={(e) => setAnnexNumber(e.target.value)}
        />
      </label>
      <label className="space-y-1">
        <span className="section-label block">Termin z aneksu</span>
        <DatePicker value={deadline} onChange={setDeadline} />
      </label>
      <Button
        size="sm"
        variant="secondary"
        loading={m.resolve.isPending}
        onClick={() =>
          m.resolve.mutate({
            id: annex.id,
            payload: {
              outcome: "signed",
              annex_number: annexNumber,
              signed_deadline: deadline || null,
            },
          })
        }
      >
        Aneks zawarty
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => m.resolve.mutate({ id: annex.id, payload: { outcome: "rejected" } })}
      >
        Odrzucony
      </Button>
    </div>
  );
}

function AnnexRow({ annex, connection }: { annex: ZEAnnexRequest; connection: string }) {
  const m = useAnnexMutations(connection);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [sentAt, setSentAt] = useState("");
  const draft = annex.draft;
  const pending = annex.draft_status === "pending";

  const patch = (payload: Partial<ZEAnnexRequest>) => m.update.mutate({ id: annex.id, payload });

  return (
    <div className="border-b border-[var(--color-border-muted)] last:border-b-0 py-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={STATUS_COLOR[annex.status]}>{annex.status_display}</Badge>
        <span className="font-medium">Wniosek o aneks nr {annex.number}</span>
        <span className="text-xs text-[var(--color-text-muted)] font-mono">
          {annex.scope_display}
          {annex.proposed_deadline ? ` · do ${fmtDate(annex.proposed_deadline)}` : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            className="text-xs text-[var(--color-primary)] hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Zwiń" : "Otwórz"}
          </button>
          <Button
            size="sm"
            variant="ghost"
            title="Usuń wniosek"
            onClick={() => {
              if (window.confirm("Usunąć ten wniosek o aneks?")) m.remove.mutate(annex.id);
            }}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {annex.status === "signed" && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Aneks {annex.annex_number || "bez numeru"} zawarty {fmtDate(annex.resolved_on)}
          {annex.signed_deadline ? ` — termin realizacji do ${fmtDate(annex.signed_deadline)}` : ""}
        </p>
      )}

      {open && (
        <div className="space-y-3 pt-1">
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="space-y-1">
              <span className="section-label block">Dotyczy</span>
              <select
                className="input-base w-full"
                value={annex.scope}
                onChange={(e) => patch({ scope: e.target.value as ZEAnnexScope })}
              >
                {(Object.keys(ZE_ANNEX_SCOPE_LABEL) as ZEAnnexScope[]).map((s) => (
                  <option key={s} value={s}>
                    {ZE_ANNEX_SCOPE_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="section-label block">Obowiązujący termin</span>
              <DatePicker
                value={annex.current_deadline ?? ""}
                onChange={(v) => patch({ current_deadline: v || null })}
              />
            </label>
            <label className="space-y-1">
              <span className="section-label block">Proponowany termin</span>
              <DatePicker
                value={annex.proposed_deadline ?? ""}
                onChange={(v) => patch({ proposed_deadline: v || null })}
              />
            </label>
            <label className="space-y-1">
              <span className="section-label block">Data wniosku</span>
              <DatePicker
                value={annex.issued_on ?? ""}
                onChange={(v) => patch({ issued_on: v || null })}
              />
            </label>
          </div>

          <div className="border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">Uzasadnienie (ARON)</span>
              {annex.draft_status === "success" && draft && (
                <Badge color={draft.ai ? "green" : "yellow"}>
                  {draft.ai ? "Napisane przez ARON" : "Zestawienie faktów — do przeredagowania"}
                </Badge>
              )}
              {annex.draft_status === "failure" && <Badge color="red">Błąd generowania</Badge>}
              {pending && <Badge color="gray">Generowanie…</Badge>}
              <div className="ml-auto flex items-center gap-2">
                {annex.draft_file?.url && (
                  <a
                    href={annex.draft_file.url}
                    download
                    className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                  >
                    <Download size={12} /> pobierz wniosek .docx
                  </a>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={m.generateDraft.isPending}
                  onClick={() => m.generateDraft.mutate(annex.id)}
                >
                  <Sparkles size={13} />
                  {annex.draft_status === "success" ? "Generuj ponownie" : "Przygotuj wniosek"}
                </Button>
              </div>
            </div>
            {annex.draft_status === "none" && !pending && (
              <p className="text-xs text-[var(--color-text-secondary)]">
                ARON zbierze całą historię zadania od zawarcia umowy, ułoży uzasadnienie
                (co zrobiono → co zostało → przyczyna niezależna od wykonawcy) i wypełni
                formularz wniosku w pliku .docx.
              </p>
            )}
            {annex.draft_status === "failure" && annex.draft_error && (
              <p className="text-xs text-[var(--color-primary)]">{annex.draft_error}</p>
            )}
            {draft && (
              <p className="text-xs text-[var(--color-text-secondary)]">
                Historia od {draft.period.from_display ?? "?"} · {draft.facts_total} zdarzeń ·
                wygenerowano {fmtDateTime(annex.draft_generated_at)}
              </p>
            )}
            <label className="space-y-1 block">
              <span className="section-label block">Treść uzasadnienia (edytowalna)</span>
              <textarea
                className="input-base w-full font-normal"
                rows={8}
                defaultValue={annex.justification}
                key={annex.draft_generated_at ?? annex.id}
                onBlur={(e) => {
                  if (e.target.value !== annex.justification) patch({ justification: e.target.value });
                }}
              />
            </label>
            <p className="text-xs text-[var(--color-text-muted)]">
              Poprawki zapisują się po wyjściu z pola. Ponowne generowanie nadpisze treść
              tylko wtedy, gdy nie była ruszana ręcznie.
            </p>
          </div>

          <Checklist rows={annex.checklist} />

          <div className="space-y-2">
            <MultiFileInput files={files} onChange={setFiles} label="Załączniki wniosku" />
            {files.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                loading={m.addFiles.isPending}
                onClick={() =>
                  m.addFiles.mutate({ id: annex.id, files }, { onSuccess: () => setFiles([]) })
                }
              >
                Dodaj pliki
              </Button>
            )}
            <AttachmentLinks files={annex.files} />
          </div>

          <div className="flex flex-wrap items-end gap-2 border-t border-[var(--color-border-muted)] pt-2">
            {annex.status === "draft" && (
              <>
                <label className="space-y-1">
                  <span className="section-label block">Data złożenia</span>
                  <DatePicker value={sentAt} onChange={setSentAt} />
                </label>
                <Button
                  size="sm"
                  loading={m.markSent.isPending}
                  onClick={() => m.markSent.mutate({ id: annex.id, sent_at: sentAt || undefined })}
                >
                  Złożony u operatora
                </Button>
              </>
            )}
            {annex.status === "sent" && (
              <>
                <ResolveForm annex={annex} connection={connection} />
                <Button size="sm" variant="ghost" onClick={() => m.unmarkSent.mutate(annex.id)}>
                  Cofnij złożenie
                </Button>
              </>
            )}
            {(annex.status === "signed" || annex.status === "rejected") && (
              <Button size="sm" variant="ghost" onClick={() => m.reopen.mutate(annex.id)}>
                Cofnij rozstrzygnięcie
              </Button>
            )}
          </div>

          {annex.notes && (
            <p className="text-xs text-[var(--color-text-secondary)] whitespace-pre-wrap">
              {annex.notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Rejestr wniosków o aneks — mieszka pod raportami, bo raport z postępu prac jest
 *  obowiązkowym załącznikiem wniosku i oba dokumenty powstają z tych samych faktów. */
export function AnnexRequests({ connection }: { connection: PowerProject }) {
  const { data: annexes } = useAnnexRequests(connection.id);
  const m = useAnnexMutations(connection.id);
  const [deadline, setDeadline] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setError(null);
    try {
      await m.create.mutateAsync({
        connection: connection.id,
        scope: "term",
        proposed_deadline: deadline || null,
      });
      setDeadline("");
    } catch {
      setError("Nie udało się dodać wniosku.");
    }
  };

  const signed = (annexes ?? []).filter((a) => a.status === "signed").length;

  return (
    <SectionCard title={`Wnioski o aneks (${annexes?.length ?? 0})`}>
      <div className="space-y-3">
        <p className="text-xs text-[var(--color-text-secondary)]">
          Zawartych aneksów: {signed}. Termin z ostatniego zawartego aneksu obowiązuje
          w metryce raportu okresowego — data z umowy zostaje nietknięta.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="section-label block">Proponowany termin realizacji</span>
            <DatePicker value={deadline} onChange={setDeadline} />
          </label>
          <Button size="sm" loading={m.create.isPending} onClick={create}>
            Nowy wniosek o aneks
          </Button>
        </div>
        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
        {annexes?.length ? (
          <div>
            {annexes.map((a) => (
              <AnnexRow key={a.id} annex={a} connection={connection.id} />
            ))}
          </div>
        ) : (
          <EmptyState>
            Brak wniosków o aneks. Załóż wniosek, gdy termin z umowy nie zostanie dotrzymany
            z przyczyn niezależnych od wykonawcy.
          </EmptyState>
        )}
      </div>
    </SectionCard>
  );
}
